import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Menu, X, Search } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import AuthModalSimplified from '@/components/AuthModalSimplified';

const NavLink = ({ children, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="text-[14.5px] font-medium text-ink-soft hover:text-clay-700 transition-colors whitespace-nowrap"
  >
    {children}
  </button>
);

const Navbar = ({ onAuthClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search-registry?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const openAuth = (mode = 'login') => {
    if (onAuthClick) {
      onAuthClick();
    } else {
      setAuthMode(mode);
      setShowAuth(true);
    }
  };

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,box-shadow,border-color] duration-300
        ${scrolled
          ? 'bg-cream-50/85 backdrop-blur-md border-b border-border/70 shadow-soft-1'
          : 'bg-transparent border-b border-transparent'}`}
    >
      <div className="container-page">
        <div className="h-[68px] flex items-center justify-between gap-6">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center group"
            data-testid="logo"
            aria-label="Joy Basket — home"
          >
            <Logo markSize={28} className="text-ink group-hover:text-clay-700 transition-colors" />
          </button>

          {/* Center nav */}
          <div className="hidden md:flex items-center gap-8">
            <NavLink onClick={() => navigate('/how-it-works')} testId="nav-how-it-works">
              How it works
            </NavLink>
            <NavLink onClick={() => navigate('/explore-gifts')} testId="nav-explore-gifts">
              Explore gifts
            </NavLink>
            <NavLink onClick={() => navigate('/ask-jb')} testId="nav-ask-jb">
              Ask JB
            </NavLink>
          </div>

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="hidden lg:flex flex-1 max-w-xs"
          >
            <div className="relative w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
              <input
                type="text"
                placeholder="Find a registry"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 text-[14px] rounded-full bg-cream-100 border border-border/70 text-ink placeholder:text-ink-muted/80 focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 transition"
              />
            </div>
          </form>

          {/* Right cluster */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  data-testid="nav-my-registries-btn"
                  className="hidden md:inline-flex h-10 px-4 rounded-full text-ink-soft hover:text-clay-700 hover:bg-cream-100 font-medium"
                >
                  My registries
                </Button>
                <Button
                  onClick={logout}
                  data-testid="nav-logout-btn"
                  className="hidden md:inline-flex h-10 px-5 rounded-full bg-ink text-cream-50 hover:bg-ink-soft font-medium"
                >
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => openAuth('login')}
                  className="hidden md:inline-flex h-10 px-4 rounded-full text-ink-soft hover:text-clay-700 hover:bg-cream-100 font-medium"
                  data-testid="nav-login-btn"
                >
                  Sign in
                </Button>
                <Button
                  onClick={() => openAuth('signup')}
                  className="hidden md:inline-flex h-10 px-5 rounded-full bg-clay-600 hover:bg-clay-700 text-cream-50 font-medium shadow-soft-1"
                  data-testid="nav-signup-btn"
                >
                  Begin a registry
                </Button>
              </>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 -mr-2 text-ink hover:text-clay-700 rounded-full"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-5 pt-2 border-t border-border/70 space-y-1.5 animate-fade-in">
            <form onSubmit={handleSearch} className="px-1 pb-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
                <input
                  type="text"
                  placeholder="Find a registry"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-11 pl-10 pr-4 text-sm rounded-full bg-cream-100 border border-border/70 text-ink placeholder:text-ink-muted/80 focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300"
                />
              </div>
            </form>

            {[
              { label: 'How it works', go: () => navigate('/how-it-works') },
              { label: 'Explore gifts', go: () => navigate('/explore-gifts') },
              { label: 'Ask JB', go: () => navigate('/ask-jb') },
            ].map((i) => (
              <button
                key={i.label}
                onClick={() => { i.go(); setMobileOpen(false); }}
                className="block w-full text-left px-4 py-2.5 text-ink-soft hover:bg-cream-100 hover:text-clay-700 font-medium rounded-lg"
              >
                {i.label}
              </button>
            ))}

            {user ? (
              <>
                <button
                  onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-2.5 text-ink-soft hover:bg-cream-100 hover:text-clay-700 font-medium rounded-lg"
                >
                  My registries
                </button>
                <button
                  onClick={() => { logout(); setMobileOpen(false); }}
                  className="block w-full text-left mt-2 px-4 py-2.5 bg-ink text-cream-50 hover:bg-ink-soft rounded-full font-medium"
                >
                  Sign out
                </button>
              </>
            ) : (
              <div className="pt-2 space-y-2">
                <button
                  onClick={() => { openAuth('login'); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-2.5 text-ink-soft hover:bg-cream-100 hover:text-clay-700 font-medium rounded-lg"
                >
                  Sign in
                </button>
                <button
                  onClick={() => { openAuth('signup'); setMobileOpen(false); }}
                  className="block w-full text-center px-4 py-3 bg-clay-600 hover:bg-clay-700 text-cream-50 rounded-full font-medium"
                >
                  Begin a registry
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showAuth && (
        <AuthModalSimplified
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode={authMode}
          onSwitchMode={(mode) => setAuthMode(mode)}
        />
      )}
    </nav>
  );
};

export default Navbar;
